import React, { useEffect, useState } from 'react';
import { Plug, Save } from 'lucide-react';
import { toast } from 'sonner';
import { waApi, errMsg } from './waApi';

const FIELDS = [
  ['META_PHONE_NUMBER_ID', 'Phone number ID', 'From Meta → WhatsApp → API Setup'],
  ['META_WABA_ID', 'WhatsApp Business Account ID', 'Same page, line below Phone number ID'],
  ['META_APP_ID', 'App ID', 'App dashboard → Settings → Basic'],
  ['META_APP_SECRET', 'App secret', 'Settings → Basic → App secret (Show)'],
  ['META_ACCESS_TOKEN', 'Permanent access token', 'Business Settings → System Users → Generate token'],
  ['META_DISPLAY_NUMBER', 'Business number (digits, e.g. 917738161354)', 'The number people will message'],
];

export const WaConnectionForm = ({ onSaved }) => {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const load = async () => { try { setInfo(await waApi.settings()); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== undefined && v !== ''));
    if (!Object.keys(body).length) return toast.info('Nothing to save');
    setSaving(true);
    try {
      const r = await waApi.saveSettings(body);
      setInfo(r); setForm({});
      toast.success(r.provider?.configured ? 'Saved — WhatsApp is connected' : 'Saved — still missing some values');
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4" data-testid="wa-connection-form">
      <div className="flex items-center gap-2 text-sm font-semibold text-zinc-100 mb-1"><Plug size={14} className="text-emerald-400" /> Meta connection</div>
      <p className="text-[11px] text-zinc-500 mb-3">Values saved here live in this server's database and are used when the deployment secret is empty. Use it to connect production or rotate a token without redeploying.</p>
      <div className="grid md:grid-cols-2 gap-3">
        {FIELDS.map(([key, label, hint]) => {
          const k = info?.keys?.[key];
          return (
            <label key={key} className="block text-xs">
              <div className="flex items-center justify-between text-zinc-400 mb-1">
                <span>{label}</span>
                {k && <span className={`text-[10px] ${k.set ? 'text-emerald-400' : 'text-amber-300'}`} data-testid={`wa-setting-state-${key}`}>{k.set ? `set · ${k.source}` : 'missing'}</span>}
              </div>
              <input value={form[key] ?? ''} onChange={e => setForm({ ...form, [key]: e.target.value })}
                placeholder={k?.set ? k.value : hint} data-testid={`wa-setting-input-${key}`}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-1.5 text-zinc-100 outline-none focus:border-emerald-500/60 font-mono" />
            </label>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-[10px] text-zinc-500">{info?.updated_at ? `Last saved ${new Date(info.updated_at).toLocaleString('en-IN')} by ${info.updated_by}` : ''}</span>
        <button onClick={save} disabled={saving} data-testid="wa-settings-save"
          className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-xl bg-[#25D366] text-black font-semibold hover:bg-[#1ebe5a] disabled:opacity-50"><Save size={12} /> {saving ? 'Saving…' : 'Save connection'}</button>
      </div>
    </div>
  );
};
